/**
 * Functions exported from this file return user–facing URLs on which
 * fock application is expected to be mounted on.
 * a.k.a. “external routes” or “mount points”
 *
 * Do not confuse with server/utils/routes.
 */
import { join as joinPath } from 'path'
import { stringify } from 'query-string'

import {
  ABO_ITEMS,
  ABO_SERVICE,
  ABOUT,
  ACCOUNT_NEWSLETTER,
  ACCOUNT_SUBSCRIPTIONS,
  ACCOUNTABILITY,
  ADD_OFFER_TO_CART,
  ADD_TO_CART,
  AGB,
  CART,
  CHECKOUT_ADDRESS_FORMAT,
  CHECKOUT_FORMAT,
  CHECKOUT_PAYMENT_FORMAT,
  CHECKOUT_REDIRECT_RETURN_FORMAT,
  CHECKOUT_SHIPPING_FORMAT,
  CHECKOUT_VERIFICATION_FORMAT,
  CLIMATE_NEUTRAL_SHIPPING,
  CMS_BRANDS,
  CONFIRMATION,
  CONTACT,
  DELIVERY_TRACKING,
  FAQ,
  FAQ_RATING_GUIDELINES,
  FAQ_SEARCH,
  FORGOT_PASSWORD,
  FORWARD,
  HOME_PAGE,
  IMPRINT,
  INCIDENT,
  LEGAL_TEXT_FORMAT,
  LOGIN,
  LOGIN_AND_REGISTER,
  LOGOUT,
  MEDCOACH,
  MESSAGE_THREAD,
  MESSAGE_THREAD_LIST,
  MYREDPOINTS,
  NEW_CART,
  NEW_CHECKOUT,
  NEW_CHECKOUT_ADDRESS,
  NEW_LOGIN,
  NEW_LOGIN_AND_REGISTER,
  NEW_REGISTER,
  NEW_USER_ACCOUNT,
  NEWSLETTER,
  NEWSLETTER_UNSUBSCRIBE,
  NOTEPAD,
  OFFERS,
  ONLINE_DOCTOR_SERVICE,
  ORDER_RETURN_REQUEST,
  ORDER_SHIPMENT_TRACKING,
  ORDERS,
  ORDERS_LIST,
  PREMIUM_DELIVERY,
  PRESCRIPTION_MANAGER,
  PRIVACY_SECURITY,
  PRIVACY_SECURITY_CREDIT_CHECK,
  PRODUCTS_BASE,
  RED_POINTS_PROMO_URL,
  REDEEM_RECIPE,
  REDPOINTS,
  REFUND,
  REGISTER,
  SEARCH,
  SWITCH_PAGE,
  TRANSITION,
  USER_ACCOUNT
} from 'shared/consts/mountPoints'
import {
  RELEASE_SWITCH_TOGGLE,
  RELEASE_TOGGLE_MARKETPLACE,
  RELEASE_TOGGLE_SAE_ONLY
} from 'shared/experiments/consts'
import { TRACKING_DATA_KEYS } from 'shared/utils/algolia/trackingDataHelper'
import { TargetPageParams } from 'types/targetPage.d'
import { OrderResponse } from 'shared/services/api/models/orders/orderResponse'

import { validate } from 'shared/services/api/helpers/pznValidator'
import { LetterboxDocument } from 'shared/services/api/models/letterbox'
import MountPointsBase, {
  QueryParamsObject
} from 'shared/utils/mountPointsBase'
import { normalizeUrl } from 'shared/utils/url'
import { format as formatUrl, parse as parseUrl } from 'url'
import { pick } from 'shared/utils/objectUtils'

const removeNxPrefix = (url: string) => url.replace('/nx', '')

class MountPoints extends MountPointsBase {
  getAboProducts(): string {
    return this.getUrl(ABO_ITEMS)
  }

  getAbout(): string {
    return this.getUrl(ABOUT)
  }

  getAccountability(): string {
    return this.getUrl(ACCOUNTABILITY)
  }

  getAccountSubscriptions(params?: QueryParamsObject): string {
    return this.setQueryParams(this.getUrl(ACCOUNT_SUBSCRIPTIONS), params)
  }

  /**
   * Convenient function that handles routes in a feature toggle dependency:
   * - legacy add to cart route (for old non offer based products). Default
   *
   * - adding offer based product to cart
   *   rendering the "cart intermediary page" where product that was added to cart is shown to user
   *   @see cartIntermediateGetHandler
   */
  getAddToCart(params: Record<string, unknown> = {}): string {
    if (
      this.isExperimentVariableEnabled(
        RELEASE_SWITCH_TOGGLE,
        RELEASE_TOGGLE_SAE_ONLY
      ) ||
      this.isExperimentVariableEnabled(
        RELEASE_SWITCH_TOGGLE,
        RELEASE_TOGGLE_MARKETPLACE
      )
    ) {
      return this.setQueryParams(this.getUrl(ADD_OFFER_TO_CART), params)
    }
    return this.setQueryParams(this.getUrl(ADD_TO_CART), params)
  }

  getAGB(): string {
    return this.getUrl(AGB)
  }

  // eslint-disable-next-line class-methods-use-this
  addCheckoutRoutingPrefix(s: string, isWebview: boolean): string[] {
    return [
      s,
      ['', `${isWebview ? 'webview/' : ''}new-checkout`]
        .concat(s.split('/').splice(4))
        .join('/'),
      ['', `${isWebview ? 'webview/' : ''}new_checkout`]
        .concat(s.split('/').splice(4))
        .join('/')
    ]
  }

  getCheckout(): string {
    return this.applyCommonRouteParams(this.getUrl(CHECKOUT_FORMAT))
  }

  getCheckoutAddress(): string {
    return this.applyCommonRouteParams(this.getUrl(CHECKOUT_ADDRESS_FORMAT))
  }

  getNewCheckoutAddress(): string {
    return this.applyCommonRouteParams(this.getUrl(NEW_CHECKOUT_ADDRESS))
  }

  getCheckoutPayment(): string {
    return this.applyCommonRouteParams(this.getUrl(CHECKOUT_PAYMENT_FORMAT))
  }

  getCheckoutShipping(): string {
    return this.applyCommonRouteParams(this.getUrl(CHECKOUT_SHIPPING_FORMAT))
  }

  getCheckoutVerification(): string {
    return this.applyCommonRouteParams(
      this.getUrl(CHECKOUT_VERIFICATION_FORMAT)
    )
  }

  getCheckoutRedirectReturn(): string {
    return this.applyCommonRouteParams(
      this.getUrl(CHECKOUT_REDIRECT_RETURN_FORMAT)
    )
  }

  getClimateNeutralShippingUrl(): string {
    return this.getUrl(CLIMATE_NEUTRAL_SHIPPING)
  }

  // eslint-disable-next-line class-methods-use-this
  getCmsAllProductsFromCategory(path: string): string {
    return normalizeUrl(joinPath(path, 'all-from-category'))
  }

  getCmsBrands(): string {
    return this.getUrl(CMS_BRANDS)
  }

  getConfirmation(orderNumber: string): string {
    return this.getUrl(CONFIRMATION).replace(':orderNumber', orderNumber)
  }

  getContact(): string {
    return this.getUrl(CONTACT)
  }

  // eslint-disable-next-line class-methods-use-this
  getContent(name: string): string {
    return normalizeUrl(`/${name}/`)
  }

  getDeliveryTracking(params: QueryParamsObject): string {
    return this.setQueryParams(this.getUrl(DELIVERY_TRACKING), params)
  }

  getFaq(): string {
    return this.getUrl(FAQ)
  }

  getFaqRatingGuidelines(): string {
    return this.getUrl(FAQ_RATING_GUIDELINES)
  }

  getFaqSearch(): string {
    return this.getUrl(FAQ_SEARCH)
  }

  getForgotPassword(): string {
    return this.getUrl(FORGOT_PASSWORD)
  }

  getForward(pathName: string): string {
    return this.getUrl(FORWARD).replace(':pathName', pathName)
  }

  getHomePage(): string {
    return this.getUrl(HOME_PAGE)
  }

  getImprint(): string {
    return this.getUrl(IMPRINT)
  }

  getLegalText(): string {
    return this.applyCommonRouteParams(this.getUrl(LEGAL_TEXT_FORMAT))
  }

  getLogout(): string {
    return this.getUrl(LOGOUT)
  }

  getLoginAndRegister(targetPageParams?: TargetPageParams): string {
    let url: string

    if (this.isNxEnabled()) {
      url = this.getUrl(NEW_LOGIN_AND_REGISTER)
    } else if (
      this.isExperimentVariableEnabled(
        RELEASE_SWITCH_TOGGLE,
        RELEASE_TOGGLE_SAE_ONLY
      )
    ) {
      url = removeNxPrefix(this.getUrl(NEW_LOGIN_AND_REGISTER))
    } else {
      url = this.getUrl(LOGIN_AND_REGISTER)
    }

    return this.setQueryParams(url, targetPageParams)
  }

  getOnlineDoctorService(id: string | number | null): string {
    return this.setQueryParams(this.getUrl(ONLINE_DOCTOR_SERVICE), {
      id: id ? String(id) : undefined
    })
  }

  getOrderDetails(baseUrl: string, order: OrderResponse): string {
    return `${baseUrl}${this.applyCommonRouteParams(this.getUrl(ORDERS))}${
      order.orderMasterData.orderNumber
    }/${order.subOrders[0].subOrderId}`
  }

  getOrderReturnRequest(): string {
    return this.getUrl(ORDER_RETURN_REQUEST)
  }

  getOrdersDetails(
    {
      orderNumber,
      subOrderNumber
    }: { orderNumber: string; subOrderNumber: string } = {
      orderNumber: '',
      subOrderNumber: ''
    }
  ): string {
    return `${this.getOrdersList()}${orderNumber}/${subOrderNumber}/`
  }

  getOrderShipmentTracking(): string {
    return this.getUrl(ORDER_SHIPMENT_TRACKING)
  }

  getOrdersList(): string {
    return this.getUrl(ORDERS_LIST)
  }

  getOrdersOverview(orderNumber = ''): string {
    return `${this.getOrdersList()}${orderNumber}/`
  }

  getPremiumDelivery(): string {
    return this.getUrl(PREMIUM_DELIVERY)
  }

  getPrivacySecurityCreditCheck(): string {
    return this.getUrl(PRIVACY_SECURITY_CREDIT_CHECK)
  }

  getPrivacySecurity(): string {
    return this.getUrl(PRIVACY_SECURITY)
  }

  // eslint-disable-next-line class-methods-use-this
  getProductReviews(url: string): string {
    const hash = '#user-reviews-section'

    if (url === '') {
      return hash
    }

    const urlObject = parseUrl(url)

    const [base, productId, ...pathParts] = urlObject.pathname
      .replace(/^\/+/, '')
      .split('/')
    const { valid } = validate(productId)

    if (!valid) {
      return null
    }

    urlObject.pathname = [base, productId, ...pathParts].join('/')

    urlObject.hash = hash

    // Remove origin
    if (process.env.APP_ENV !== 'production') {
      urlObject.protocol = ''
      urlObject.slashes = false
      urlObject.host = ''
      urlObject.hostname = ''
    }

    return normalizeUrl(formatUrl(urlObject))
  }

  getProduct(id: number | string): string {
    return normalizeUrl(joinPath(this.getUrl(PRODUCTS_BASE), String(id)))
  }

  getProductUrl(displayName: string, id: number | string): string {
    return normalizeUrl(
      joinPath(
        this.getUrl(PRODUCTS_BASE),
        String(id),
        displayName.toLowerCase().replace(/\s/g, '-').concat('.htm')
      )
    )
  }

  getRedPointsPromoUrl(): string {
    return this.getUrl(RED_POINTS_PROMO_URL)
  }

  getRegister(targetPageParams?: TargetPageParams): string {
    let url: string

    if (this.isNxEnabled()) {
      url = this.getUrl(NEW_REGISTER)
    } else if (
      this.isExperimentVariableEnabled(
        RELEASE_SWITCH_TOGGLE,
        RELEASE_TOGGLE_SAE_ONLY
      )
    ) {
      url = removeNxPrefix(this.getUrl(NEW_REGISTER))
    } else {
      url = this.getUrl(REGISTER)
    }

    return this.setQueryParams(url, targetPageParams)
  }

  getSearch({
    query,
    escapeQuery = true,
    internal = true,
    channel = null,
    userToken = null,
    trackingData = null
  }: {
    query: string
    escapeQuery?: boolean
    internal?: boolean
    channel?: string | null
    userToken?: string | null
    trackingData?: Record<
      typeof TRACKING_DATA_KEYS[number],
      string | number | undefined
    > | null
  }): string {
    let params: Record<string, any> = {}

    if (internal) {
      params.i = 1
    }

    if (query != null) {
      params.q = query
    }

    if (channel) {
      params.searchChannel = channel
    }

    if (userToken) {
      params.userToken = userToken
    }

    if (trackingData) {
      params = { ...params, ...pick(trackingData, TRACKING_DATA_KEYS) }
    }

    let route = `${this.getUrl(SEARCH)}?${stringify(params)}`

    if (!escapeQuery) {
      route = route.replace(/(\?|&)q=[^&]*/, `$1q=${query}`)
    }

    return route
  }

  getSearchProductsList(products: { id: string }[]): string {
    const produtIds = products.map(({ id }) => String(id)).join(',')
    return this.getSearch({ query: produtIds, internal: false })
  }

  getTransition(targetUrl: string): string {
    const queryString = `?${stringify({ target: targetUrl })}`
    return `${this.getUrl(TRANSITION)}${queryString}`
  }

  getNewsletter(): string {
    return this.getUrl(NEWSLETTER)
  }

  getRefund(orderId: string, subOrderId: string): string {
    return `${this.getUrl(REFUND)}${orderId}/${subOrderId}`
  }

  getIncident(orderId: string, subOrderId: string): string {
    return `${this.getUrl(INCIDENT)}${orderId}/${subOrderId}`
  }

  getRedpoints(): string {
    return this.getUrl(REDPOINTS)
  }

  getMyRedpoints(): string {
    return this.getUrl(MYREDPOINTS)
  }

  getNewsletterUnsubscribe(): string {
    return this.getUrl(NEWSLETTER_UNSUBSCRIBE)
  }

  getLogin(targetPageParams?: TargetPageParams): string {
    let url: string

    if (this.isNxEnabled()) {
      url = this.getUrl(NEW_LOGIN)
    } else if (
      this.isExperimentVariableEnabled(
        RELEASE_SWITCH_TOGGLE,
        RELEASE_TOGGLE_SAE_ONLY
      )
    ) {
      url = removeNxPrefix(this.getUrl(NEW_LOGIN))
    } else {
      url = this.getUrl(LOGIN)
    }

    return this.setQueryParams(url, targetPageParams)
  }

  getNewCheckout(): string {
    return this.applyCommonRouteParams(this.getUrl(NEW_CHECKOUT))
  }

  getUserAccount(): string {
    return this.getUrl(USER_ACCOUNT)
  }

  getMessageThread(threadId = '') {
    return `${this.getUrl(MESSAGE_THREAD)}${threadId}${threadId ? '/' : ''}`
  }

  getMessageThreadList() {
    return this.getUrl(MESSAGE_THREAD_LIST)
  }

  getMessageDownloadDocument({ attachmentId = '', threadId = '' }) {
    return `${this.getUrl(MESSAGE_THREAD)}${threadId}/${attachmentId}/`
  }

  getNewUserAccount(): string {
    return this.getUrl(NEW_USER_ACCOUNT)
  }

  getCart() {
    return this.applyCommonRouteParams(this.getUrl(CART))
  }

  getNewCart(params: QueryParamsObject = {}) {
    return this.applyCommonRouteParams(
      this.setQueryParams(this.getUrl(NEW_CART), params)
    )
  }

  getNotepad(params = {}): string {
    return this.setQueryParams(this.getUrl(NOTEPAD), params)
  }

  getNotepadEntry(upid: string): string {
    return `${this.getUrl(NOTEPAD)}${upid}`
  }

  getNotepadDeleteEntry(upid: string): string {
    return `${this.getUrl(NOTEPAD)}${upid}/delete`
  }

  getLetterboxDocumentURL(document: LetterboxDocument) {
    return `${this.getUrl(ORDERS_LIST)}${document.orderId}/document/${
      document.type
    }/${document.content.hash}`
  }

  getMedcoach() {
    return this.getUrl(MEDCOACH)
  }

  getAboService() {
    return this.getUrl(ABO_SERVICE)
  }

  getRedeemRecipe(hash: string = '') {
    return `${this.getUrl(REDEEM_RECIPE)}${hash}`
  }

  getPrescriptionManager() {
    return this.getUrl(PRESCRIPTION_MANAGER)
  }

  getOffersUrl(offerId: string) {
    return `${this.getUrl(OFFERS)}${offerId}/`
  }

  getSwitchPageUrl(release: string) {
    return `${this.getUrl(SWITCH_PAGE)}${release}`
  }

  getContinueShoppingPage(productId: number | string): string {
    if (productId) {
      return this.getProduct(productId)
    } else {
      return this.getHomePage()
    }
  }

  getAccountNewsletter() {
    return this.getUrl(ACCOUNT_NEWSLETTER)
  }

  getAccountNewsletterSubscribe(): string {
    return `${this.getUrl(ACCOUNT_NEWSLETTER)}subscribe/`
  }

  getAccountNewsletterUnsubscribe(): string {
    return `${this.getUrl(ACCOUNT_NEWSLETTER)}unsubscribe/`
  }
}

export default MountPoints
